import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { addIdMapper } from 'utils/helpers';
import { editRestaurantReducer } from './editRestaurantReducer';
import { AppContext, IAppContext } from 'appState/appContext';
import { EditRestaurantActionTypes } from './editRestaurantActions';
import {
	IEditRestaurantState,
	EditRestaurantContext,
	editRestaurantInitialState,
	TEditRestaurantVisibleForm,
	TTypeOfEditRestaurantModalWindow,
	IFormMccCard,
	IExtraPaymentType,
} from './editRestaurantContext';
import {
	ICurrencies,
	IItemForGetShortInfo,
	IGetRestaurantInfo,
	IRestaurantBankSettingsAcquirer,
	IUpdateRestaurant,
	TAdditionalApplications,
	TMenuContentVersion,
	TPosSystemId,
	CardBrands,
	TUseTipsByDefault,
	TTypeOfServiceProvided,
	TTlsCertificateLevel,
} from 'types';
import { getRestaurantInfo } from 'backend/restaurants';
import { useFetchData } from 'hooks/useFetchData';

export const EditRestaurantState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editRestaurantReducer, editRestaurantInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const restaurantID = params.get('restaurantID');

	const { response, loading, refetch } = useFetchData<IGetRestaurantInfo, string>(
		restaurantID ? getRestaurantInfo : null,
		restaurantID || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditRestaurantActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useNamedHeader(response?.data.restaurant.name);

	useEffect(() => {
		if (response) {
			const tips: (number | null)[] = [];
			let defaultTips: TUseTipsByDefault = null;
			let countOfTips = 0;
			while (countOfTips !== 4) {
				tips.push(
					response.data.restaurant.defaultTips[countOfTips]
						? response.data.restaurant.defaultTips[countOfTips].value
						: null,
				);
				if (
					response.data.restaurant.defaultTips?.[countOfTips] &&
					response.data.restaurant.defaultTips[countOfTips].isDefault
				) {
					defaultTips = String(countOfTips) as TUseTipsByDefault;
				}
				countOfTips++;
			}
			const charityAmountsResponse = response.data.restaurant.charityAmounts ?? [];
			const charityAmounts = [0, 1, 2, 3].map((index) => charityAmountsResponse[index]?.amount ?? null);
			const foundDefaultCharityAmountIndex = charityAmountsResponse.findIndex((el) => el.isDefault);
			const defaultCharityAmountIndex =
				foundDefaultCharityAmountIndex === -1 ? null : foundDefaultCharityAmountIndex;
			const formattedMasterPhone =
				response.data.restaurant.masterPhoneNumber?.replace(
					/(\+7)(\d{3})(\d{3})(\d{2})(\d{2})/,
					'$1 $2 $3 $4 $5',
				) || '';
			const formattedSupplierPhone =
				response.data.restaurant.supplier?.phone?.replace(
					/(\+7)(\d{3})(\d{3})(\d{2})(\d{2})/,
					'$1 $2 $3 $4 $5',
				) || '';

			const extraPaymentTypes = Object.keys(response.data.restaurant.extraPaymentTypes).map(
				(key, idx): IExtraPaymentType => ({
					id: String(idx),
					brand: CardBrands[key as keyof typeof CardBrands],
					paymentTypeId: response.data.restaurant.extraPaymentTypes[key],
				}),
			);

			if (extraPaymentTypes.length === 0) {
				extraPaymentTypes.push({ id: '0', brand: null, paymentTypeId: null });
			}

			dispatch({
				type: EditRestaurantActionTypes.SET_ALL_DATA,
				payload: {
					restaurantId: response.data.restaurant.id,
					name: response.data.restaurant.name,
					shortName: response.data.restaurant.shortName,
					IDSelectedCustomer: response.data.restaurant.customerId,
					clientType: response.data.restaurant.restaurantType,
					IDSelectedCountry: response.data.restaurant.countryId,
					IDSelectedCity: response.data.restaurant.cityId,
					address: response.data.restaurant.address,
					selectedCurrencySymbol: response.data.restaurant.isoCurrencySymbol,
					masterPhoneNumber: formattedMasterPhone,
					email: response.data.restaurant.email || '',
					code: response.data.restaurant.code || '',
					additionalApplications: response.data.restaurant.additionalApplications,
					vivaWalletIsv: response.data.restaurant.vivaWalletIsv
						? {
								sourceId: response.data.restaurant.vivaWalletIsv.sourceId,
								isvSourceId: response.data.restaurant.vivaWalletIsv.isvSourceId || '',
								isvPercent: response.data.restaurant.vivaWalletIsv.isvPercent,
								isvFixedFeeAmount: response.data.restaurant.vivaWalletIsv.isvFixedFeeAmount,
								isvFixedFeeThreshold: response.data.restaurant.vivaWalletIsv.isvFixedFeeThreshold,
								pinCode: response.data.restaurant.vivaWalletIsv.pinCode || '111111',
								franchiseFeeSupported:
									response.data.restaurant.vivaWalletIsv.franchiseCommission !== null,
								franchisePercent:
									response.data.restaurant.vivaWalletIsv.franchiseCommission?.percent ?? null,
								franchiseFixedFeeAmount:
									response.data.restaurant.vivaWalletIsv.franchiseCommission?.fixedAmount ?? null,
								franchiseFixedFeeThreshold:
									response.data.restaurant.vivaWalletIsv.franchiseCommission?.fixedAmountThreshold ??
									null,
						  }
						: {
								sourceId: '',
								isvSourceId: '',
								isvPercent: null,
								isvFixedFeeAmount: null,
								isvFixedFeeThreshold: null,
								pinCode: '111111',
								franchiseFeeSupported: false,
								franchisePercent: null,
								franchiseFixedFeeAmount: null,
								franchiseFixedFeeThreshold: null,
						  },
					supplier: {
						name: response.data.restaurant.supplier?.name || '',
						taxpayerId: response.data.restaurant.supplier?.taxpayerId || '',
						phone: formattedSupplierPhone,
					},
					isOpenPriceServiceCharge: response.data.restaurant.isOpenPriceServiceCharge || false,
					openPricePercent: response.data.restaurant.openPricePercent,
					priceType: response.data.restaurant.priceType,
					isUsedCustomerPriceType: response.data.restaurant.isUsedCustomerPriceType,
					loyalty: response.data.restaurant.loyalty !== null ? String(response.data.restaurant.loyalty) : '',
					isUsedCustomerTips: response.data.restaurant.isUsedCustomerTips,
					tips0: tips[0],
					tips1: tips[1],
					tips2: tips[2],
					tips3: tips[3],
					defaultTips,
					useKmTariff: response.data.restaurant.commissionTariff === 'KATEMEDIA',
					tipsCommissionPercent: response.data.restaurant.tipsCommissionPercent,
					tipsCommissionMaxValue: response.data.restaurant.tipsCommissionMaxValue,
					isCharityEnabled: response.data.restaurant.isCharityEnabled,
					charityName: response.data.restaurant.charityName || '',
					charityLogoPath: response.data.restaurant.charityLogoPath,
					charityLogoFileId: '',
					charityAmount0: charityAmounts[0],
					charityAmount1: charityAmounts[1],
					charityAmount2: charityAmounts[2],
					charityAmount3: charityAmounts[3],
					defaultCharityAmountIndex,
					isShowCustomerLoyaltyProgramButtons: response.data.restaurant.customer.isShowLoyaltyProgramButtons,
					isShowLoyaltyProgramButtons: response.data.restaurant.isShowLoyaltyProgramButtons,
					baseMcc: response.data.restaurant.merchantCategoryCode?.baseMcc
						? response.data.restaurant.merchantCategoryCode.baseMcc
						: null,
					mainMccCardBrand: response.data.restaurant.merchantCategoryCode?.mccCards[0]?.brand || null,
					mainMccCardMcc: response.data.restaurant.merchantCategoryCode?.mccCards[0]?.mcc
						? response.data.restaurant.merchantCategoryCode.mccCards[0].mcc
						: null,
					mccCards: response.data.restaurant.merchantCategoryCode?.mccCards.slice(1).map(addIdMapper) || [],
					useCustomerMcc: response.data.restaurant.useCustomerMcc,
					downloadAppAndroidUrl: response.data.restaurant.downloadAppAndroidUrl || '',
					downloadAppIosUrl: response.data.restaurant.downloadAppIosUrl || '',
					IDDefaultLanguage: response.data.restaurant.defaultLanguageId,
					IDsLanguages: response.data.restaurant.restaurantLanguages.map((el) => el.languageId),
					restaurantLanguages: response.data.restaurant.restaurantLanguages,
					restrictionQrValue: response.data.restaurant.restrictionQrValue || '',
					usePosPhotos: response.data.restaurant.usePosPhotos,
					enableStopListJob: response.data.restaurant.syncStopList,
					useCustomerStopListSyncInterval: response.data.restaurant.useCustomerStopListSyncInterval,
					stopListSyncIntervalMinutes: response.data.restaurant.stopListSyncIntervalMinutes,
					updateOfTheLauncher: response.data.restaurant.updateOfTheLauncher,
					cardAcceptorId: response.data.restaurant.cardAcceptorId
						? response.data.restaurant.cardAcceptorId
						: '',
					cardAcceptorName: response.data.restaurant.cardAcceptorName
						? response.data.restaurant.cardAcceptorName
						: '',
					terminalId: response.data.restaurant.terminalId ? response.data.restaurant.terminalId : '',
					vat: response.data.restaurant.terminal !== null ? String(response.data.restaurant.terminal) : '',
					timeBlockChildAccess:
						response.data.restaurant.timeBlock !== null ? String(response.data.restaurant.timeBlock) : '',
					IDPosSystem: response.data.restaurant.posSystemId,
					launcherSettingFilePath: response.data.restaurant.launcherSettingFilePath,
					generatedWatchCommandsLink: response.data.restaurant.generatedWatchCommandsLink,
					useMenuVersion: response.data.restaurant.useMenuVersion,
					useContentVersion: response.data.restaurant.useContentVersion,
					customerMenuVersion: response.data.restaurant.customerMenuVersion || '',
					customerContentVersion: response.data.restaurant.customerContentVersion || '',
					restaurantMenuVersion: response.data.restaurant.restaurantMenuVersion || '',
					restaurantContentVersion: response.data.restaurant.restaurantContentVersion || '',
					useTlsCertificate: response.data.restaurant.useTlsCertificate,
					tlsCertificateLevel: response.data.restaurant.tlsCertificateLevel,
					tlsCertificateName: response.data.restaurant.tlsCertificateName,
					tlsCertificateFilePath: response.data.restaurant.tlsCertificateFilePath,
					tlsCertificateFileId: '',
					vatId: response.data.restaurant.vatId || '',
					kmVatId: response.data.restaurant.kmVatId || '',
					isActiveBpa: response.data.restaurant.customer.isActiveBpa,
					bpaSetId: response.data.restaurant.bpaSet?.id || null,
					isApplicableForVat: response.data.restaurant.isApplicableForVat,
					sendDeviceUnavailabilityAlerts: response.data.restaurant.sendDeviceUnavailabilityAlerts,
					paymentTypeId: response.data.restaurant.paymentTypeId,
					extraPaymentTypes: extraPaymentTypes,
				},
			});
			dispatch({
				type: EditRestaurantActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						name: response.data.restaurant.name,
						shortName: response.data.restaurant.shortName,
						IDSelectedCustomer: response.data.restaurant.customerId,
						clientType: response.data.restaurant.restaurantType,
						IDSelectedCountry: response.data.restaurant.countryId,
						IDSelectedCity: response.data.restaurant.cityId,
						address: response.data.restaurant.address,
						selectedCurrencySymbol: response.data.restaurant.isoCurrencySymbol,
						masterPhoneNumber: formattedMasterPhone,
						email: response.data.restaurant.email || '',
						code: response.data.restaurant.code || '',
						additionalApplications: response.data.restaurant.additionalApplications,
						vivaWalletIsv: response.data.restaurant.vivaWalletIsv
							? {
									sourceId: response.data.restaurant.vivaWalletIsv.sourceId,
									isvSourceId: response.data.restaurant.vivaWalletIsv.isvSourceId || '',
									isvPercent: response.data.restaurant.vivaWalletIsv.isvPercent,
									isvFixedFeeAmount: response.data.restaurant.vivaWalletIsv.isvFixedFeeAmount,
									isvFixedFeeThreshold: response.data.restaurant.vivaWalletIsv.isvFixedFeeThreshold,
									pinCode: response.data.restaurant.vivaWalletIsv.pinCode || '111111',
									franchiseFeeSupported:
										response.data.restaurant.vivaWalletIsv.franchiseCommission !== null,
									franchisePercent:
										response.data.restaurant.vivaWalletIsv.franchiseCommission?.percent ?? null,
									franchiseFixedFeeAmount:
										response.data.restaurant.vivaWalletIsv.franchiseCommission?.fixedAmount ?? null,
									franchiseFixedFeeThreshold:
										response.data.restaurant.vivaWalletIsv.franchiseCommission
											?.fixedAmountThreshold ?? null,
							  }
							: {
									sourceId: '',
									isvSourceId: '',
									isvPercent: null,
									isvFixedFeeAmount: null,
									isvFixedFeeThreshold: null,
									pinCode: '111111',
									franchiseFeeSupported: false,
									franchisePercent: null,
									franchiseFixedFeeAmount: null,
									franchiseFixedFeeThreshold: null,
							  },
						priceType: response.data.restaurant.priceType,
						supplier: {
							name: response.data.restaurant.supplier?.name || '',
							taxpayerId: response.data.restaurant.supplier?.taxpayerId || '',
							phone: formattedSupplierPhone,
						},
						isOpenPriceServiceCharge: response.data.restaurant.isOpenPriceServiceCharge || false,
						openPricePercent: response.data.restaurant.openPricePercent,
						isUsedCustomerPriceType: response.data.restaurant.isUsedCustomerPriceType,
						loyalty:
							response.data.restaurant.loyalty !== null ? String(response.data.restaurant.loyalty) : '',
						isUsedCustomerTips: response.data.restaurant.isUsedCustomerTips,
						tips0: tips[0],
						tips1: tips[1],
						tips2: tips[2],
						tips3: tips[3],
						defaultTips,
						useKmTariff: response.data.restaurant.commissionTariff === 'KATEMEDIA',
						tipsCommissionPercent: response.data.restaurant.tipsCommissionPercent,
						tipsCommissionMaxValue: response.data.restaurant.tipsCommissionMaxValue,
						isCharityEnabled: response.data.restaurant.isCharityEnabled,
						charityName: response.data.restaurant.charityName || '',
						charityLogoPath: response.data.restaurant.charityLogoPath,
						charityLogoFileId: '',
						charityAmount0: charityAmounts[0],
						charityAmount1: charityAmounts[1],
						charityAmount2: charityAmounts[2],
						charityAmount3: charityAmounts[3],
						defaultCharityAmountIndex,
						isShowLoyaltyProgramButtons: response.data.restaurant.isShowLoyaltyProgramButtons,
						baseMcc: response.data.restaurant.merchantCategoryCode?.baseMcc
							? response.data.restaurant.merchantCategoryCode.baseMcc
							: null,
						mainMccCardBrand: response.data.restaurant.merchantCategoryCode?.mccCards[0]?.brand || null,
						mainMccCardMcc: response.data.restaurant.merchantCategoryCode?.mccCards[0]?.mcc
							? response.data.restaurant.merchantCategoryCode.mccCards[0].mcc
							: null,
						mccCards: response.data.restaurant.merchantCategoryCode?.mccCards.slice(1) || [],
						useCustomerMcc: response.data.restaurant.useCustomerMcc,
						downloadAppAndroidUrl: response.data.restaurant.downloadAppAndroidUrl || '',
						downloadAppIosUrl: response.data.restaurant.downloadAppIosUrl || '',
						IDDefaultLanguage: response.data.restaurant.defaultLanguageId,
						IDsLanguages: response.data.restaurant.restaurantLanguages.map((el) => el.languageId),
						restrictionQrValue: response.data.restaurant.restrictionQrValue || '',
						usePosPhotos: response.data.restaurant.usePosPhotos,
						enableStopListJob: response.data.restaurant.syncStopList,
						useCustomerStopListSyncInterval: response.data.restaurant.useCustomerStopListSyncInterval,
						stopListSyncIntervalMinutes: response.data.restaurant.stopListSyncIntervalMinutes,
						updateOfTheLauncher: response.data.restaurant.updateOfTheLauncher,
						cardAcceptorId: response.data.restaurant.cardAcceptorId
							? response.data.restaurant.cardAcceptorId
							: '',
						cardAcceptorName: response.data.restaurant.cardAcceptorName
							? response.data.restaurant.cardAcceptorName
							: '',
						terminalId: response.data.restaurant.terminalId ? response.data.restaurant.terminalId : '',
						vat:
							response.data.restaurant.terminal !== null ? String(response.data.restaurant.terminal) : '',
						timeBlockChildAccess:
							response.data.restaurant.timeBlock !== null
								? String(response.data.restaurant.timeBlock)
								: '',
						IDPosSystem: response.data.restaurant.posSystemId,
						launcherSettingFilePath: response.data.restaurant.launcherSettingFilePath,
						useMenuVersion: response.data.restaurant.useMenuVersion,
						useContentVersion: response.data.restaurant.useContentVersion,
						restaurantMenuVersion: response.data.restaurant.restaurantMenuVersion || '',
						restaurantContentVersion: response.data.restaurant.restaurantContentVersion || '',
						useTlsCertificate: response.data.restaurant.useTlsCertificate,
						tlsCertificateLevel: response.data.restaurant.tlsCertificateLevel,
						tlsCertificateName: response.data.restaurant.tlsCertificateName,
						tlsCertificateFilePath: response.data.restaurant.tlsCertificateFilePath,
						tlsCertificateFileId: '',
						vatId: response.data.restaurant.vatId || '',
						kmVatId: response.data.restaurant.kmVatId || '',
						bpaSetId: response.data.restaurant.bpaSet?.id || null,
						isApplicableForVat: response.data.restaurant.isApplicableForVat,
						sendDeviceUnavailabilityAlerts: response.data.restaurant.sendDeviceUnavailabilityAlerts,
						paymentTypeId: response.data.restaurant.paymentTypeId,
						extraPaymentTypes: extraPaymentTypes,
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditRestaurantActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TEditRestaurantVisibleForm) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const changeShortName = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_SHORT_NAME,
			payload: {
				shortName: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setClientType = useCallback((value: Nullable<TTypeOfServiceProvided>) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_CLIENT_TYPE,
			payload: {
				clientType: value,
			},
		});
	}, []);

	const setFetchedCountries = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_FETCHED_COUNTRIES,
			payload: {
				fetchedCountries: value,
			},
		});
	}, []);

	const setIDCountry = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_ID_COUNTRY,
			payload: {
				IDSelectedCountry: value,
			},
		});
	}, []);

	const setFetchedCities = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_FETCHED_CITIES,
			payload: {
				fetchedCities: value,
			},
		});
	}, []);

	const setIDCity = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_ID_CITY,
			payload: {
				IDSelectedCity: value,
			},
		});
	}, []);

	const changeAddress = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_ADDRESS,
			payload: {
				address: value,
			},
		});
	}, []);

	const setFetchedCurrencies = useCallback((value: ICurrencies[]) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_FETCHED_CURRENCIES,
			payload: {
				fetchedCurrencies: value,
			},
		});
	}, []);

	const setCurrencySymbol = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_CURRENCY_SYMBOL,
			payload: {
				selectedCurrencySymbol: value,
			},
		});
	}, []);

	const changeMasterPhoneNumber = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_MASTER_PHONE_NUMBER,
			payload: {
				masterPhoneNumber: value,
			},
		});
	}, []);

	const changeEmail = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_EMAIL,
			payload: {
				email: value,
			},
		});
	}, []);

	const changeCode = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_CODE,
			payload: {
				code: value,
			},
		});
	}, []);

	const setAdditionalApplications = useCallback(
		(value: TAdditionalApplications[]) => {
			const hasVivaWallet = value.includes('VivaWallet');

			dispatch({
				type: EditRestaurantActionTypes.SET_ADDITIONAL_APPLICATIONS,
				payload: {
					additionalApplications: value,
					vivaWalletIsv: hasVivaWallet
						? state.vivaWalletIsv
						: {
								sourceId: '',
								isvSourceId: '',
								isvPercent: null,
								isvFixedFeeAmount: null,
								isvFixedFeeThreshold: null,
								pinCode: '111111',
								franchiseFeeSupported: false,
								franchisePercent: null,
								franchiseFixedFeeAmount: null,
								franchiseFixedFeeThreshold: null,
						  },
				},
			});
		},
		[state.vivaWalletIsv],
	);

	const changeVivaWalletIsv = useCallback(
		({ key, value }: { key: keyof IEditRestaurantState['vivaWalletIsv']; value?: string | number | null }) => {
			if (key === 'franchiseFeeSupported') {
				const franchiseData = state.vivaWalletIsv.franchiseFeeSupported
					? {
							franchiseFeeSupported: false,
							franchisePercent: null,
							franchiseFixedFeeAmount: null,
							franchiseFixedFeeThreshold: null,
					  }
					: { franchiseFeeSupported: true };

				dispatch({
					type: EditRestaurantActionTypes.CHANGE_VIVA_WALLET_ISV,
					payload: {
						vivaWalletIsv: { ...state.vivaWalletIsv, ...franchiseData },
					},
				});
			} else {
				dispatch({
					type: EditRestaurantActionTypes.CHANGE_VIVA_WALLET_ISV,
					payload: {
						vivaWalletIsv: { ...state.vivaWalletIsv, [key]: value },
					},
				});
			}
		},
		[state.vivaWalletIsv],
	);

	const changeSupplier = useCallback(
		({ key, value }: { key: keyof IEditRestaurantState['supplier']; value: string }) => {
			dispatch({
				type: EditRestaurantActionTypes.CHANGE_SUPPLIER,
				payload: {
					supplier: { ...state.supplier, [key]: value },
				},
			});
		},
		[state.supplier],
	);

	const toggleIsOpenPriceServiceCharge = useCallback(() => {
		dispatch({ type: EditRestaurantActionTypes.TOGGLE_IS_OPEN_PRICE_PERCENT });
	}, []);

	const changeOpenPricePercent = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_OPEN_PRICE_PERCENT,
			payload: {
				openPricePercent: value,
			},
		});
	}, []);

	const changePriceType = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_PRICE_TYPE,
			payload: {
				priceType: value,
			},
		});
	}, []);

	const toggleIsUsedCustomerPriceType = useCallback(() => {
		dispatch({
			type: EditRestaurantActionTypes.TOGGLE_IS_USED_CUSTOMER_PRICE_TYPE,
			payload: {
				isUsedCustomerPriceType: !state.isUsedCustomerPriceType,
				priceType: state.isUsedCustomerPriceType ? state.priceType : null,
			},
		});
	}, [state.isUsedCustomerPriceType, state.priceType]);

	const changeLoyalty = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_LOYALTY,
			payload: {
				loyalty: value,
			},
		});
	}, []);

	const toggleIsUsedCustomerTips = useCallback(() => {
		if (state.isUsedCustomerTips) {
			dispatch({
				type: EditRestaurantActionTypes.TOGGLE_IS_USED_CUSTOMER_TIPS,
				payload: {
					isUsedCustomerTips: false,
					tips0: state.tips0,
					tips1: state.tips1,
					tips2: state.tips2,
					tips3: state.tips3,
					defaultTips: state.defaultTips,
				},
			});
		} else {
			dispatch({
				type: EditRestaurantActionTypes.TOGGLE_IS_USED_CUSTOMER_TIPS,
				payload: {
					isUsedCustomerTips: true,
					tips0: null,
					tips1: null,
					tips2: null,
					tips3: null,
					defaultTips: null,
				},
			});
		}
	}, [state.isUsedCustomerTips, state.tips0, state.tips1, state.tips2, state.tips3, state.defaultTips]);

	const setTips0 = useCallback((value: null | number) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_TIPS_0,
			payload: {
				tips0: value,
			},
		});
	}, []);

	const setTips1 = useCallback((value: null | number) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_TIPS_1,
			payload: {
				tips1: value,
			},
		});
	}, []);

	const setTips2 = useCallback((value: null | number) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_TIPS_2,
			payload: {
				tips2: value,
			},
		});
	}, []);

	const setTips3 = useCallback((value: null | number) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_TIPS_3,
			payload: {
				tips3: value,
			},
		});
	}, []);

	const setDefaultTips = useCallback(
		(value: TUseTipsByDefault) => {
			dispatch({
				type: EditRestaurantActionTypes.SET_DEFAULT_TIPS,
				payload: {
					defaultTips: state.defaultTips === value ? null : value,
				},
			});
		},
		[state.defaultTips],
	);

	const setTipsCommissionPercent = useCallback(
		(value: Nullable<number>) => {
			dispatch({
				type: EditRestaurantActionTypes.SET_TIPS_COMMISSION_PERCENT,
				payload: {
					tipsCommissionPercent: value,
					tipsCommissionMaxValue: value !== null ? state.tipsCommissionMaxValue : null,
				},
			});
		},
		[state.tipsCommissionMaxValue],
	);

	const setTipsCommissionMaxValue = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_TIPS_COMMISSION_MAX_VALUE,
			payload: {
				tipsCommissionMaxValue: value,
			},
		});
	}, []);

	const toggleIsCharityEnabled = useCallback(() => {
		dispatch({ type: EditRestaurantActionTypes.TOGGLE_IS_CHARITY_ENABLED });
	}, []);

	const changeCharityName = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_CHARITY_NAME,
			payload: {
				charityName: value,
			},
		});
	}, []);

	const setCharityLogoFile = useCallback((fileId: Nullable<string>) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_CHARITY_LOGO_FILE,
			payload: {
				charityLogoFileId: fileId,
			},
		});
	}, []);

	const setCharityAmount0 = useCallback(
		(value: null | number) => {
			dispatch({
				type: EditRestaurantActionTypes.SET_CHARITY_AMOUNT_0,
				payload: {
					charityAmount0: value,
					defaultCharityAmountIndex:
						value === null && state.defaultCharityAmountIndex === 0
							? null
							: state.defaultCharityAmountIndex,
				},
			});
		},
		[state.defaultCharityAmountIndex],
	);

	const setCharityAmount1 = useCallback(
		(value: null | number) => {
			dispatch({
				type: EditRestaurantActionTypes.SET_CHARITY_AMOUNT_1,
				payload: {
					charityAmount1: value,
					defaultCharityAmountIndex:
						value === null && state.defaultCharityAmountIndex === 1
							? null
							: state.defaultCharityAmountIndex,
				},
			});
		},
		[state.defaultCharityAmountIndex],
	);

	const setCharityAmount2 = useCallback(
		(value: null | number) => {
			dispatch({
				type: EditRestaurantActionTypes.SET_CHARITY_AMOUNT_2,
				payload: {
					charityAmount2: value,
					defaultCharityAmountIndex:
						value === null && state.defaultCharityAmountIndex === 2
							? null
							: state.defaultCharityAmountIndex,
				},
			});
		},
		[state.defaultCharityAmountIndex],
	);

	const setCharityAmount3 = useCallback(
		(value: null | number) => {
			dispatch({
				type: EditRestaurantActionTypes.SET_CHARITY_AMOUNT_3,
				payload: {
					charityAmount3: value,
					defaultCharityAmountIndex:
						value === null && state.defaultCharityAmountIndex === 3
							? null
							: state.defaultCharityAmountIndex,
				},
			});
		},
		[state.defaultCharityAmountIndex],
	);

	const setDefaultCharityAmountIndex = useCallback(
		(value: number) => {
			dispatch({
				type: EditRestaurantActionTypes.SET_DEFAULT_CHARITY_AMOUNT_INDEX,
				payload: {
					defaultCharityAmountIndex: state.defaultCharityAmountIndex === value ? null : value,
				},
			});
		},
		[state.defaultCharityAmountIndex],
	);

	const toggleUseKmTariff = useCallback(() => {
		dispatch({ type: EditRestaurantActionTypes.TOGGLE_USE_KM_TARIFF });
	}, []);

	const toggleShowLoyaltyProgramButtons = useCallback(() => {
		dispatch({ type: EditRestaurantActionTypes.TOGGLE_SHOW_LOYALTY_PROGRAM_BUTTONS });
	}, []);

	const changeBaseMcc = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_BASE_MCC,
			payload: {
				baseMcc: value,
			},
		});
	}, []);

	const addMccCard = useCallback(
		(value: IFormMccCard) => {
			const updatedMccCards = [...state.mccCards, value];

			dispatch({
				type: EditRestaurantActionTypes.ADD_MCC_CARD,
				payload: {
					mccCards: updatedMccCards,
				},
			});
		},
		[state.mccCards],
	);

	const deleteMccCard = useCallback(
		(id: string) => {
			const updatedMccCards = state.mccCards.filter((el) => el.id !== id);

			dispatch({
				type: EditRestaurantActionTypes.DELETE_MCC_CARD,
				payload: {
					mccCards: updatedMccCards,
				},
			});
		},
		[state.mccCards],
	);

	const changeMainMccCardBrand = useCallback((value: CardBrands) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_MAIN_MCC_CARD_BRAND,
			payload: {
				mainMccCardBrand: value,
			},
		});
	}, []);

	const changeMccCardBrand = useCallback(
		(id: string, value: CardBrands) => {
			const updatedMccCards = state.mccCards.map((el) => (el.id === id ? { ...el, brand: value } : el));

			dispatch({
				type: EditRestaurantActionTypes.CHANGE_MCC_CARD_BRAND,
				payload: {
					mccCards: updatedMccCards,
				},
			});
		},
		[state.mccCards],
	);

	const changeMainMccCardMcc = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_MAIN_MCC_CARD_MCC,
			payload: {
				mainMccCardMcc: value,
			},
		});
	}, []);

	const changeMccCardMcc = useCallback(
		(id: string, value: Nullable<number>) => {
			const updatedMccCards = state.mccCards.map((el) => (el.id === id ? { ...el, mcc: value } : el));

			dispatch({
				type: EditRestaurantActionTypes.CHANGE_MCC_CARD_MCC,
				payload: {
					mccCards: updatedMccCards,
				},
			});
		},
		[state.mccCards],
	);

	const toggleUseCustomerMcc = useCallback(() => {
		dispatch({
			type: EditRestaurantActionTypes.TOGGLE_USE_CUSTOMER_MCC,
		});
	}, []);

	const changeDownloadAppAndroidUrl = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_DOWNLOAD_APP_ANDROID_URL,
			payload: {
				downloadAppAndroidUrl: value,
			},
		});
	}, []);

	const changeDownloadAppIosUrl = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_DOWNLOAD_APP_IOS_URL,
			payload: {
				downloadAppIosUrl: value,
			},
		});
	}, []);

	const setFetchedLanguages = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_FETCHED_LANGUAGES,
			payload: {
				fetchedLanguages: value,
			},
		});
	}, []);

	const setIDDefaultLanguage = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_ID_DEFAULT_LANGUAGE,
			payload: {
				IDDefaultLanguage: value,
			},
		});
	}, []);

	const setIDsLanguages = useCallback((value: string[]) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_IDs_LANGUAGES,
			payload: {
				IDsLanguages: value,
			},
		});
	}, []);

	const changeRestrictionQrValue = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_RESTRICTION_QR_VALUE,
			payload: {
				restrictionQrValue: value,
			},
		});
	}, []);

	const toggleUsePosPhotos = useCallback(() => {
		dispatch({ type: EditRestaurantActionTypes.TOGGLE_USE_POS_PHOTOS });
	}, []);

	const toggleStopListJob = useCallback(() => {
		dispatch({ type: EditRestaurantActionTypes.TOGGLE_STOP_LIST_JOB });
	}, []);

	const toggleUseCustomerStopListSyncInterval = useCallback(() => {
		dispatch({ type: EditRestaurantActionTypes.TOGGLE_USE_CUSTOMER_STOP_LIST_SYNC_INTERVAL });
	}, []);

	const changeStopListSyncIntervalMinutes = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_STOP_LIST_SYNC_INTERVAL_MINUTES,
			payload: {
				stopListSyncIntervalMinutes: value,
			},
		});
	}, []);

	const toggleUpdateOfTheLauncher = useCallback(() => {
		dispatch({ type: EditRestaurantActionTypes.TOGGLE_UPDATE_OF_THE_LAUNCHER });
	}, []);

	const changeCardAcceptorId = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_CARD_ACCEPTOR_ID,
			payload: {
				cardAcceptorId: value,
			},
		});
	}, []);

	const changeCardAcceptorName = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_CARD_ACCEPTOR_NAME,
			payload: {
				cardAcceptorName: value,
			},
		});
	}, []);

	const changeTerminalId = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_TERMINAL_ID,
			payload: {
				terminalId: value,
			},
		});
	}, []);

	const changeVat = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_VAT,
			payload: {
				vat: value,
			},
		});
	}, []);

	const changeTimeBlockChildAccess = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_TIME_BLOCK_CHILD_ACCESS,
			payload: {
				timeBlockChildAccess: value,
			},
		});
	}, []);

	const setIDPosSystem = useCallback((value: Nullable<TPosSystemId>) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_ID_POS_SYSTEM,
			payload: {
				IDPosSystem: value,
			},
		});
	}, []);

	const setFileIdForLauncherSettings = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_FILE_ID_FOR_LAUNCHER_SETTINGS,
			payload: {
				fileIdForLauncherSettings: value,
			},
		});
	}, []);

	const setFetchedAcquirers = useCallback((value: IRestaurantBankSettingsAcquirer[]) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_FETCHED_ACQUIRERS,
			payload: {
				fetchedAcquirers: value,
			},
		});
	}, []);

	const toggleUseMenuVersion = useCallback(
		(value: TMenuContentVersion) => {
			dispatch({
				type: EditRestaurantActionTypes.TOGGLE_USE_MENU_VERSION,
				payload: {
					useMenuVersion: value,
					restaurantMenuVersion:
						value === 'Customer' ? state.customerMenuVersion : state.restaurantMenuVersion,
				},
			});
		},
		[state.customerMenuVersion, state.restaurantMenuVersion],
	);

	const toggleUseContentVersion = useCallback(
		(value: TMenuContentVersion) => {
			dispatch({
				type: EditRestaurantActionTypes.TOGGLE_USE_CONTENT_VERSION,
				payload: {
					useContentVersion: value,
					restaurantContentVersion:
						value === 'Customer' ? state.customerContentVersion : state.restaurantContentVersion,
				},
			});
		},
		[state.customerContentVersion, state.restaurantContentVersion],
	);

	const changeRestaurantMenuVersion = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_RESTAURANT_MENU_VERSION,
			payload: {
				restaurantMenuVersion: value,
			},
		});
	}, []);

	const changeRestaurantContentVersion = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_RESTAURANT_CONTENT_VERSION,
			payload: {
				restaurantContentVersion: value,
			},
		});
	}, []);

	const toggleUseTLSCertificate = useCallback(() => {
		dispatch({ type: EditRestaurantActionTypes.TOGGLE_USE_TLS_CERTIFICATE });
	}, []);

	const setTlsCertificateLevel = useCallback((value: Nullable<TTlsCertificateLevel>) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_TLS_CERTIFICATE_LEVEL,
			payload: {
				tlsCertificateLevel: value,
			},
		});
	}, []);

	const changeTlsCertificateName = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_TLS_CERTIFICATE_NAME,
			payload: {
				tlsCertificateName: value,
			},
		});
	}, []);

	const setTlsCertificateFile = useCallback(
		({ fileId, fileHash }: { fileId: Nullable<string>; fileHash: Nullable<string> }) => {
			dispatch({
				type: EditRestaurantActionTypes.SET_TLS_CERTIFICATE_FILE,
				payload: {
					tlsCertificateFileId: fileId,
					tlsCertificateFileHash: fileHash,
				},
			});
		},
		[],
	);

	const changeKmVatId = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_KM_VAT_ID,
			payload: {
				kmVatId: value,
			},
		});
	}, []);

	const changeVatId = useCallback((value: string) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_VAT_ID,
			payload: {
				vatId: value,
			},
		});
	}, []);

	const setBpaSetId = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_BPA_SET_ID,
			payload: {
				bpaSetId: value,
			},
		});
	}, []);

	const toggleApplicableForVat = useCallback(() => {
		dispatch({ type: EditRestaurantActionTypes.TOGGLE_APPLICABLE_FOR_VAT });
	}, []);

	const toggleSendDeviceUnavailabilityAlerts = useCallback(() => {
		dispatch({ type: EditRestaurantActionTypes.TOGGLE_SEND_DEVICE_UNAVAILABILITY_ALERTS });
	}, []);

	const changePaymentTypeId = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_PAYMENT_TYPE_ID,
			payload: {
				paymentTypeId: value,
			},
		});
	}, []);

	const addExtraPaymentType = useCallback(
		(value: IExtraPaymentType) => {
			const updatedExtraPaymentTypeIds = [...state.extraPaymentTypes, value];

			dispatch({
				type: EditRestaurantActionTypes.CHANGE_EXTRA_PAYMENT_TYPE,
				payload: {
					extraPaymentTypes: updatedExtraPaymentTypeIds,
				},
			});
		},
		[state.extraPaymentTypes],
	);

	const changeExtraPaymentType = useCallback(
		(id: string, value: IExtraPaymentType) => {
			const updatedExtraPaymentTypeIds = state.extraPaymentTypes.map((el) => (el.id === id ? value : el));

			dispatch({
				type: EditRestaurantActionTypes.CHANGE_EXTRA_PAYMENT_TYPE,
				payload: {
					extraPaymentTypes: updatedExtraPaymentTypeIds,
				},
			});
		},
		[state.extraPaymentTypes],
	);

	const deleteExtraPaymentType = useCallback(
		(id: string) => {
			const updatedExtraPaymentTypeIds = state.extraPaymentTypes.filter((el) => el.id !== id);

			dispatch({
				type: EditRestaurantActionTypes.CHANGE_EXTRA_PAYMENT_TYPE,
				payload: {
					extraPaymentTypes: updatedExtraPaymentTypeIds,
				},
			});
		},
		[state.extraPaymentTypes],
	);

	const setDataForSubmit = useCallback((value: IUpdateRestaurant | null) => {
		dispatch({
			type: EditRestaurantActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditRestaurantModalWindow) => {
		dispatch({
			type: EditRestaurantActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditRestaurantActionTypes.RESET_FIELDS }), []);

	return (
		<EditRestaurantContext.Provider
			value={{
				refetchData,
				changeVisibleForm,
				changeName,
				changeShortName,
				setFetchedCustomers,
				setIDCustomer,
				setClientType,
				setFetchedCountries,
				setIDCountry,
				setFetchedCities,
				setIDCity,
				changeAddress,
				setFetchedCurrencies,
				setCurrencySymbol,
				changeMasterPhoneNumber,
				changeEmail,
				changeCode,
				setAdditionalApplications,
				changeVivaWalletIsv,
				changeSupplier,
				toggleIsOpenPriceServiceCharge,
				changeOpenPricePercent,
				changePriceType,
				toggleIsUsedCustomerPriceType,
				changeLoyalty,
				toggleIsUsedCustomerTips,
				setTips0,
				setTips1,
				setTips2,
				setTips3,
				setDefaultTips,
				setTipsCommissionPercent,
				setTipsCommissionMaxValue,
				toggleIsCharityEnabled,
				changeCharityName,
				setCharityLogoFile,
				setCharityAmount0,
				setCharityAmount1,
				setCharityAmount2,
				setCharityAmount3,
				setDefaultCharityAmountIndex,
				toggleUseKmTariff,
				toggleShowLoyaltyProgramButtons,
				changeBaseMcc,
				addMccCard,
				deleteMccCard,
				changeMainMccCardBrand,
				changeMccCardBrand,
				changeMainMccCardMcc,
				changeMccCardMcc,
				toggleUseCustomerMcc,
				changeDownloadAppAndroidUrl,
				changeDownloadAppIosUrl,
				setFetchedLanguages,
				setIDDefaultLanguage,
				setIDsLanguages,
				changeRestrictionQrValue,
				toggleUsePosPhotos,
				toggleStopListJob,
				toggleUseCustomerStopListSyncInterval,
				changeStopListSyncIntervalMinutes,
				toggleUpdateOfTheLauncher,
				changeCardAcceptorId,
				changeCardAcceptorName,
				changeTerminalId,
				changeVat,
				changeTimeBlockChildAccess,
				setIDPosSystem,
				setFileIdForLauncherSettings,
				setFetchedAcquirers,
				toggleUseMenuVersion,
				toggleUseContentVersion,
				changeRestaurantMenuVersion,
				changeRestaurantContentVersion,
				toggleUseTLSCertificate,
				setTlsCertificateLevel,
				changeTlsCertificateName,
				setTlsCertificateFile,
				changeVatId,
				changeKmVatId,
				setBpaSetId,
				toggleApplicableForVat,
				toggleSendDeviceUnavailabilityAlerts,
				changePaymentTypeId,
				addExtraPaymentType,
				changeExtraPaymentType,
				deleteExtraPaymentType,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				visibleForm: state.visibleForm,
				restaurantId: state.restaurantId,
				name: state.name,
				shortName: state.shortName,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				clientType: state.clientType,
				fetchedCountries: state.fetchedCountries,
				IDSelectedCountry: state.IDSelectedCountry,
				fetchedCities: state.fetchedCities,
				IDSelectedCity: state.IDSelectedCity,
				address: state.address,
				fetchedCurrencies: state.fetchedCurrencies,
				selectedCurrencySymbol: state.selectedCurrencySymbol,
				masterPhoneNumber: state.masterPhoneNumber,
				email: state.email,
				code: state.code,
				additionalApplications: state.additionalApplications,
				vivaWalletIsv: state.vivaWalletIsv,
				supplier: state.supplier,
				isOpenPriceServiceCharge: state.isOpenPriceServiceCharge,
				openPricePercent: state.openPricePercent,
				priceType: state.priceType,
				isUsedCustomerPriceType: state.isUsedCustomerPriceType,
				loyalty: state.loyalty,
				isUsedCustomerTips: state.isUsedCustomerTips,
				tips0: state.tips0,
				tips1: state.tips1,
				tips2: state.tips2,
				tips3: state.tips3,
				defaultTips: state.defaultTips,
				tipsCommissionPercent: state.tipsCommissionPercent,
				tipsCommissionMaxValue: state.tipsCommissionMaxValue,
				isCharityEnabled: state.isCharityEnabled,
				charityName: state.charityName,
				charityLogoPath: state.charityLogoPath,
				charityLogoFileId: state.charityLogoFileId,
				charityAmount0: state.charityAmount0,
				charityAmount1: state.charityAmount1,
				charityAmount2: state.charityAmount2,
				charityAmount3: state.charityAmount3,
				defaultCharityAmountIndex: state.defaultCharityAmountIndex,
				useKmTariff: state.useKmTariff,
				isShowCustomerLoyaltyProgramButtons: state.isShowCustomerLoyaltyProgramButtons,
				isShowLoyaltyProgramButtons: state.isShowLoyaltyProgramButtons,
				baseMcc: state.baseMcc,
				mainMccCardBrand: state.mainMccCardBrand,
				mainMccCardMcc: state.mainMccCardMcc,
				mccCards: state.mccCards,
				useCustomerMcc: state.useCustomerMcc,
				downloadAppAndroidUrl: state.downloadAppAndroidUrl,
				downloadAppIosUrl: state.downloadAppIosUrl,
				fetchedLanguages: state.fetchedLanguages,
				IDDefaultLanguage: state.IDDefaultLanguage,
				IDsLanguages: state.IDsLanguages,
				restaurantLanguages: state.restaurantLanguages,
				restrictionQrValue: state.restrictionQrValue,
				usePosPhotos: state.usePosPhotos,
				enableStopListJob: state.enableStopListJob,
				useCustomerStopListSyncInterval: state.useCustomerStopListSyncInterval,
				stopListSyncIntervalMinutes: state.stopListSyncIntervalMinutes,
				updateOfTheLauncher: state.updateOfTheLauncher,
				cardAcceptorId: state.cardAcceptorId,
				cardAcceptorName: state.cardAcceptorName,
				terminalId: state.terminalId,
				vat: state.vat,
				timeBlockChildAccess: state.timeBlockChildAccess,
				IDPosSystem: state.IDPosSystem,
				fileIdForLauncherSettings: state.fileIdForLauncherSettings,
				launcherSettingFilePath: state.launcherSettingFilePath,
				fetchedAcquirers: state.fetchedAcquirers,
				generatedWatchCommandsLink: state.generatedWatchCommandsLink,
				useMenuVersion: state.useMenuVersion,
				useContentVersion: state.useContentVersion,
				customerMenuVersion: state.customerMenuVersion,
				customerContentVersion: state.customerContentVersion,
				restaurantMenuVersion: state.restaurantMenuVersion,
				restaurantContentVersion: state.restaurantContentVersion,
				useTlsCertificate: state.useTlsCertificate,
				tlsCertificateLevel: state.tlsCertificateLevel,
				tlsCertificateName: state.tlsCertificateName,
				tlsCertificateFilePath: state.tlsCertificateFilePath,
				tlsCertificateFileId: state.tlsCertificateFileId,
				tlsCertificateFileHash: state.tlsCertificateFileHash,
				vatId: state.vatId,
				kmVatId: state.kmVatId,
				isActiveBpa: state.isActiveBpa,
				bpaSetId: state.bpaSetId,
				isApplicableForVat: state.isApplicableForVat,
				sendDeviceUnavailabilityAlerts: state.sendDeviceUnavailabilityAlerts,
				paymentTypeId: state.paymentTypeId,
				extraPaymentTypes: state.extraPaymentTypes,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditRestaurantContext.Provider>
	);
};
